import React from 'react';
import i18n from 'i18n-js';

import { localTime, isWeb, withERP } from '@helpers';
import { font } from '@styles/fonts';
import Svg from '@styles/icons/svg';

import DateHeader from '@components/machines/components/timeline/components/DateHeader';

import Jobs from '@components/machines/components/timeline/components/Jobs';
import Alarms from '@components/machines/components/timeline/components/Alarms';
import Timeline from '@components/machines/components/timeline/components/Timeline';

import FeatureFlags from '@components/featureFlags';
import LabelPopup from '@components/machines/components/timeline/components/LabelPopup';
import ColorWrapper from '@styles/colors';
import { SCREEN_BORDER_RADIUS, SCREEN_PADDING_HORIZONTAL, SCREEN_PADDING_VERTICAL_FOR_ITEM } from '@v2/pages/common/constants/base.constants';
import { useHandleParams } from '@v2/pages/machines/hooks/useHandleParams';
import { NCFileSearchItem } from '@interfaces/search';
import BatchTimeLineTrack from '@v2/pages/batch/details/modules/batch/components/BatchTimeLineTrack/BatchTimeLineTrack';
import { useHandleMainDetailsScreenNavigation } from '@v2/pages/common/hooks/useHandleMainDetailsScreenNavigation';
import { NCFileDetailsScreenName } from '@v2/pages/nc-file/details/navigation/config';
import { NCFileDetailsScreen } from '@v2/pages/nc-file/details/common/enums/ncFileDetails.enum';
import { Perspective } from '@v2/pages/common/enums';
import Counts from '@components/machines/components/timeline/components/Counts';
import NCFileDetailsModalWrapper from '@components/wallboard/components/NCFileDetailsModalWrapper';
import { fullWidth, width as fullWidthMobile, scale } from '@components/frame';
import { TimelineBlockTitle } from '@components/machines/components/timeline/components/TimelineBlockTitle';
import { TIMELINE_CONFIG, TIMELINE_LINE_CONFIG } from '@components/machines/components/timeline/constants';
import TimelineShimmer from '@components/machines/components/timeline/components/TimelineShimmer';
import {
  useBatchTimelineSizes,
  useBatchTimelineViewModel,
  useBatchTimelineSelectors,
  useBatchTimelineEffects,
  useBatchTimelineRange,
  useBatchTimelineActions,
} from '../../hooks';
import { WELCOME_TOUR_IDS } from '@components/tours/config/global/welcome/constants';
import { useManageTimelinePopup } from '@components/machines/components/timeline/hooks/useManageTimelinePopup';
import { useManageTimelineActions } from '@components/machines/components/timeline/hooks/useManageTimelineActions';
import { setSelectedNCFile as setSelectedNCFileManufacture } from '@reducers/manufacture';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { ColorFunction } from '@styles/colors/types';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';

export interface BatchTimelineProps {
  C: ColorFunction;
  width?: number;
  horizontalOffset?: number;
  withoutList?: boolean;
  shouldSetEndTimeAsNow?: boolean;
}

const BatchTimeline = ({
  C,
  shouldSetEndTimeAsNow,
  width = isWeb ? fullWidth : fullWidthMobile,
  withoutList = false,
  horizontalOffset = 0,
}: BatchTimelineProps) => {
  const dispatch = useAppDispatch();
  const { navigateToMainDetailsScreen } = useHandleMainDetailsScreenNavigation();

  const { timePeriod: selectedTime } = useHandleParams();

  const { batch, ncFileData, isWithERP, updatedMachineEventsTimelineHash } = useBatchTimelineSelectors();

  const {
    productionBatchStartDateForDisplay,
    setProductionBatchStartDateForDisplay,
    productionBatchEndDateForDisplay,
    setProductionBatchEndDateForDisplay,
    productionBatchStartDate,
    productionBatchEndDate,
    setProductionBatchEndDate,
    isEndDateForDisplayAndActualEndDateIsTheSame,
    isStartDateForDisplayAndActualStartDateIsTheSame,
    isZoomOutDisabled,
    isZoomResetDisabled,
  } = useBatchTimelineRange({ batch, shouldSetEndTimeAsNow });

  const {
    detailsWidth,
    blockWidth,
    popupWidthProductivityStatus,
    popupWidthProductivityStatusLabel,
    popupWidthProductivityStatusLabelSymbolSize,
    popupHeightProductivityStatusLabel,
    popupWidthAlarm,
    popupWidthAlarmSymbolSize,
    popupHeightAlarm,
    popupWidthNcFileName,
    popupWidthNcFileNameSymbolSize,
    popupHeightNcFileName,
  } = useBatchTimelineSizes({ width, withoutList, horizontalOffset });

  const {
    fetchData,
    abortControllerEvents,
    abortControllerAlarmEvent,
    abortControllerCountsSearch,
    events,
    alarmEventGroups,
    countsData,
    productionBatchId,
  } = useBatchTimelineViewModel({
    productionBatchStartDateForDisplay,
    productionBatchEndDateForDisplay,
  });

  const {
    popupConfigAlarm,
    popupConfigNcFileName,
    clearPopupTimeout,
    popupConfigProductivityStatus,
    popupConfigProductivityStatusLabel,
    triggerHidePopup,
    triggerShowPopup,
  } = useManageTimelinePopup({
    sizes: {
      blockWidth,
      popupWidthProductivityStatus,
      popupWidthProductivityStatusLabel,
      popupWidthProductivityStatusLabelSymbolSize,
      popupWidthAlarm,
      popupWidthAlarmSymbolSize,
      popupWidthNcFileName,
      popupWidthNcFileNameSymbolSize,
    },
  });

  const {
    abortControllerEventStart,
    abortControllerEventEnd,
    abortControllerNcFile,
    rules,
    timelineRef,
    lineRef,
    sectionRef,
    timeXRef,
    handleMouseMove,
    selectRange,
    onMouseLeave,
    handleMouseDown,
    handleMouseUp,
  } = useManageTimelineActions({
    startTime: productionBatchStartDateForDisplay,
    endTime: productionBatchEndDateForDisplay,
    yearIsEnabled: true,
    fetchData,
    triggerHidePopup,
    sizes: { blockWidth },
  });

  useBatchTimelineEffects({
    abortControllerEvents,
    abortControllerAlarmEvent,
    abortControllerCountsSearch,
    abortControllerEventStart,
    abortControllerEventEnd,
    abortControllerNcFile,
    productionBatchId,
    productionBatchStartDateForDisplay,
    productionBatchEndDateForDisplay,
    updatedMachineEventsTimelineHash,
    fetchData,
  });

  // Batch timeline actions hook
  const { handleSelectRange, zoomReset } = useBatchTimelineActions({
    productionBatchStartDate,
    setProductionBatchStartDateForDisplay,
    setProductionBatchEndDateForDisplay,
    setProductionBatchEndDate,
    selectRange,
  });

  return (
    <div
      data-testid={TEST_IDS.batchTimeline.container}
      style={{
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        userSelect: 'none',
        paddingTop: SCREEN_PADDING_VERTICAL_FOR_ITEM,
        width: detailsWidth - SCREEN_PADDING_HORIZONTAL * 2,
        marginInline: 'auto',
        paddingInline: SCREEN_PADDING_HORIZONTAL,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          background: C('Background/Light/Field'),
          borderRadius: scale(SCREEN_BORDER_RADIUS),
          boxShadow: '0px 0px 6px rgba(0, 0, 0, 0.1)',
        }}
      >
        <DateHeader
          {...{
            startTime: productionBatchStartDateForDisplay ?? localTime().subtract(1, 'h').format(),
            endTime: productionBatchEndDateForDisplay ?? localTime().format(),
            C,
            setSelectedChartDate: () => {},
            setSelectedChartDateEnd: () => {},
            selectedTime,
            isZoomInDisabled: rules.isZoomInDisabled,
            isZoomOutDisabled: isZoomOutDisabled,
            isZoomResetDisabled: isZoomResetDisabled,
            setEndTime: setProductionBatchEndDateForDisplay,
            setStartTime: setProductionBatchStartDateForDisplay,
            fetchData,
            selectRange: handleSelectRange,
            selectedTimeCounter: 0,
            withoutSelectedPeriodButton: true,
            resetOnInitialRender: false,
            leftArrowDisabled: isStartDateForDisplayAndActualStartDateIsTheSame,
            rightArrowDisabled: isEndDateForDisplayAndActualEndDateIsTheSame,
            zoomReset,
            minTime: productionBatchStartDate,
            maxTime: productionBatchEndDate,
          }}
        />
        <div style={{ width: blockWidth, height: '100%', position: 'relative' }}>
          <div
            id="mainContainer"
            style={{ position: 'relative', width: blockWidth, height: '100%' }}
            onMouseMove={handleMouseMove}
            onMouseLeave={onMouseLeave}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
          >
            <div
              ref={lineRef}
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                width: 1,
                backgroundColor: C('System/Light/Red50%'),
                zIndex: 1000,
              }}
            >
              <Svg icon="triangle" color="System/Light/Red" height={9} width={13} style={{ position: 'absolute', left: -6 }} />
              <div
                ref={timeXRef}
                style={{
                  ...font['Subhead 1'],
                  fontSize: 15,
                  fontWeight: 400,
                  lineHeight: '20px',
                  width: 44,
                  textAlign: 'center',
                  backgroundColor: C('System/Light/Red'),
                  color: C('Label/Light/AlwaysLight Primary'),
                  zIndex: 1,
                  position: 'absolute',
                  top: 13,
                  left: -26,
                  borderRadius: 4,
                  fontVariant: ['tabular-nums'],
                  justifyContent: 'center',
                  alignContent: 'center',
                  alignItems: 'center',
                  display: 'flex',
                  paddingLeft: 4,
                  paddingRight: 4,
                }}
              />
            </div>
            <div
              ref={sectionRef}
              style={{
                position: 'absolute',
                height: '100%',
                backgroundColor: 'rgba(0, 120, 215, 0.5)',
                pointerEvents: 'none',
                zIndex: 1000,
              }}
            />
            <div style={{ width: '100%', height: 1, background: C('Gray/Light/3') }} />
            <Timeline
              {...{
                C,
                startTime: productionBatchStartDateForDisplay ?? localTime().subtract(1, 'h').format(),
                timelineRef,
                blockWidth,
                rules,
              }}
            />
            <div data-tour-id={WELCOME_TOUR_IDS.TIMELINE_COUNTS}>
              <Counts
                {...{
                  C,
                  countsData,
                  blockWidth,
                  startTime: productionBatchStartDateForDisplay,
                  rules,
                  withLeftValueLimitation: true,
                  useIndexAsCountText: true,
                }}
              />
              <div data-tour-id={WELCOME_TOUR_IDS.TIMELINE_OVERVIEW}>
                <TimelineBlockTitle C={C}>{i18n.t('Productivity Status')}</TimelineBlockTitle>
                <BatchTimeLineTrack
                  {...{
                    C,
                    productionBatchId,
                    height: TIMELINE_LINE_CONFIG.height,
                    borderRadius: TIMELINE_LINE_CONFIG.borderRadius,
                    triggerShowPopup,
                    triggerHidePopup,
                    timelineRef,
                    ncFileId: batch?.data?.ncFileId,
                    start: productionBatchStartDateForDisplay,
                    end: productionBatchEndDateForDisplay,
                    setEnd: (endTime: string) => {
                      setProductionBatchEndDate(endTime);
                      setProductionBatchEndDateForDisplay(endTime);
                    },
                    handleMouseMove,
                    events: events?.events,
                  }}
                />
              </div>
            </div>
            {popupConfigProductivityStatus.show && productionBatchId && batch.data && (
              <NCFileDetailsModalWrapper
                {...{
                  setSelectedNCFile: (ncFile: NCFileSearchItem) => dispatch(setSelectedNCFileManufacture(ncFile)),
                  C,
                  popupType: 'productivityStatus',
                  popupConfig: popupConfigProductivityStatus,
                  popupWidth: popupWidthProductivityStatus,
                  clearPopupTimeout,
                  triggerHidePopup,
                  abortControllerEventStart,
                  abortControllerEventEnd,
                  abortControllerNcFile,
                  batchData: batch.data,
                  shouldNotLoadBatch: true,
                  ncFileData: ncFileData.data,
                }}
              />
            )}
            {popupConfigProductivityStatusLabel.show && (
              <LabelPopup
                {...{
                  C,
                  popupName: 'productivityStatusLabel',
                  popupConfig: popupConfigProductivityStatusLabel,
                  popupWidth:
                    (popupConfigProductivityStatusLabel?.item?.ncFileMain?.length ?? 0) * popupWidthProductivityStatusLabelSymbolSize +
                    popupWidthProductivityStatusLabel,
                  popupHeight: popupHeightProductivityStatusLabel,
                  clearPopupTimeout,
                  triggerHidePopup,
                }}
              />
            )}

            <TimelineBlockTitle containerStyle={{ marginTop: TIMELINE_CONFIG.gapBetweenBlocks }} C={C}>
              {i18n.t('Alarms')}
            </TimelineBlockTitle>
            {!alarmEventGroups.data.length && alarmEventGroups.loaded && (
              <div
                style={{
                  width: '100%',
                  height: 20,
                  backgroundColor: C('Background/Light/Chart'),
                  borderRadius: TIMELINE_LINE_CONFIG.borderRadius,
                }}
              />
            )}
            {alarmEventGroups.loaded ? (
              alarmEventGroups?.data?.map((alarmEvents) => (
                <Alarms
                  {...{
                    C,
                    triggerShowPopup,
                    triggerHidePopup,
                    alarmEvents,
                    blockWidth,
                    startTime: productionBatchStartDateForDisplay,
                    rules,
                    timelineRef,
                  }}
                />
              ))
            ) : (
              <TimelineShimmer style={{ height: TIMELINE_LINE_CONFIG.height, borderRadius: TIMELINE_LINE_CONFIG.borderRadius }} />
            )}
            {popupConfigAlarm.show && (
              <LabelPopup
                {...{
                  C,
                  popupName: 'alarm',
                  popupConfig: popupConfigAlarm,
                  popupWidth: (popupConfigAlarm?.item?.errorText?.length ?? 0) * popupWidthAlarmSymbolSize + popupWidthAlarm,
                  popupHeight: popupHeightAlarm,
                  clearPopupTimeout,
                  triggerHidePopup,
                  textColor: 'Label/Light/Primary',
                }}
              />
            )}
            {withERP(isWithERP) && (
              <FeatureFlags {...{ name: 'timeline-jobs' }}>
                <Jobs
                  {...{
                    C,
                    blockWidth,
                    startTime: productionBatchStartDateForDisplay,
                    rules,
                    batches: { loaded: batch.wasLoaded, data: batch.data ? [batch.data] : [] },
                    events,
                  }}
                />
              </FeatureFlags>
            )}
            {popupConfigNcFileName.show && (
              <LabelPopup
                {...{
                  C,
                  popupName: 'ncFileName',
                  popupConfig: popupConfigNcFileName,
                  popupWidth: popupConfigNcFileName?.item?.ncFileMain?.length * popupWidthNcFileNameSymbolSize + popupWidthNcFileName,
                  popupHeight: popupHeightNcFileName,
                  clearPopupTimeout,
                  triggerHidePopup,
                  action: (data: string) => {
                    navigateToMainDetailsScreen({
                      mainScreen: NCFileDetailsScreenName,
                      subScreen: NCFileDetailsScreen.NC_FILE,
                      params: {
                        perspective: Perspective.NC_FILE,
                        ncFileName: popupConfigNcFileName?.item?.ncFileMain,
                        ncFileId: popupConfigNcFileName?.item?.ncFileIdMain,
                        ncFileStatus: popupConfigNcFileName?.item?.ncFileStatus,
                      },
                    });
                  },
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ColorWrapper(BatchTimeline);
